import { apiClient } from "./client";

// Release 1.5.0 notification centre (O-R55). Every notice is recorded; groups, counts and snooze times are computed.
// A notice only ever opens a page: the person signs on the target page, never from the notice.

export type NoticeActionKind = "sign" | "open" | "work-order" | "viper";

export interface NoticeAction {
  kind: NoticeActionKind;
  label: string;
  href: string | null;
  question: string | null;
}

export interface Notice {
  id: number;
  kind: string;
  typeLabel: string;
  title: string;
  body: string;
  link: string | null;
  createdAtUtc: string;
  isRead: boolean;
  snoozedUntilUtc: string | null;
  actions: NoticeAction[];
}

export interface NoticeGroup {
  key: string;
  groupKind: "work-order" | "topic";
  label: string;
  workOrderKey: string | null;
  unreadCount: number;
  newestAtUtc: string;
  notices: Notice[];
}

export interface NotificationCentre {
  unreadCount: number;
  snoozedCount: number;
  totalShown: number;
  groups: NoticeGroup[];
  valueKinds: Record<string, string>;
}

export interface NotificationCount {
  unreadCount: number;
  valueKind: string;
}

export type SnoozeOption = "1h" | "tomorrow" | "next-workday" | "wake";

export interface SnoozeResult {
  changed: number;
  snoozedUntilUtc: string | null;
  message: string;
}

export type EmailMode = "off" | "now" | "digest";

export interface NoticePreference {
  kind: string;
  group: string;
  groupLabel: string;
  label: string;
  description: string;
  emailMode: EmailMode;
  teamsEnabled: boolean;
  canSwitchOffEmail: boolean;
  canSwitchOffTeams: boolean;
  canDigest: boolean;
  isDefault: boolean;
}

export interface NoticePreferences {
  digestTime: string;
  teamsStatus: string;
  items: NoticePreference[];
}

export interface NoticePreferenceUpdate {
  kind: string;
  emailMode: EmailMode;
  teamsEnabled: boolean;
}

export interface NoticeType {
  kind: string;
  group: string;
  label: string;
  description: string;
  canSwitchOffEmail: boolean;
  canSwitchOffTeams: boolean;
  defaultEmail: boolean;
  defaultTeams: boolean;
  defaultDigest: boolean;
  active: boolean;
  stored: boolean;
  updatedAtUtc: string | null;
  updatedBy: string | null;
}

export interface UpdateNoticeTypeRequest {
  label?: string | null;
  description?: string | null;
  defaultEmail?: boolean | null;
  defaultTeams?: boolean | null;
  defaultDigest?: boolean | null;
  active?: boolean | null;
  reason: string;
}

export const NOTIFY_QK = {
  root: ["notification-centre"] as const,
  list: (snoozed: boolean) => ["notification-centre", "list", snoozed] as const,
  count: ["notification-centre", "count"] as const,
  preferences: ["notification-centre", "preferences"] as const,
  noticeTypes: ["admin", "notifications", "notice-types"] as const
};

export const SNOOZE_CHOICES: { option: Exclude<SnoozeOption, "wake">; label: string }[] = [
  { option: "1h", label: "For 1 hour" },
  { option: "tomorrow", label: "Until tomorrow 06:00" },
  { option: "next-workday", label: "Until the next workday" }
];

export const EMAIL_MODE_LABELS: Record<EmailMode, string> = {
  off: "Off",
  now: "Straight away",
  digest: "Daily digest"
};

export const notificationCentreApi = {
  get: async (snoozed = false): Promise<NotificationCentre> =>
    (await apiClient.get<NotificationCentre>("/notification-centre", { params: snoozed ? { snoozed: true } : undefined })).data,

  count: async (): Promise<NotificationCount> => (await apiClient.get<NotificationCount>("/notification-centre/count")).data,

  markRead: async (ids: number[]): Promise<{ changed: number }> =>
    (await apiClient.post<{ changed: number }>("/notification-centre/read", { ids })).data,

  markAllRead: async (): Promise<{ changed: number }> =>
    (await apiClient.post<{ changed: number }>("/notification-centre/read-all")).data,

  /** O-R67 Undo of "Mark read" / "Mark all read": the same notices back to unread (release 1.5.0 polish). */
  markUnread: async (ids: number[]): Promise<{ changed: number }> =>
    (await apiClient.post<{ changed: number }>("/notification-centre/unread", { ids })).data,

  snooze: async (ids: number[], option: SnoozeOption): Promise<SnoozeResult> =>
    (await apiClient.post<SnoozeResult>("/notification-centre/snooze", { ids, option })).data,

  getPreferences: async (): Promise<NoticePreferences> =>
    (await apiClient.get<NoticePreferences>("/notification-centre/preferences")).data,

  updatePreferences: async (items: NoticePreferenceUpdate[]): Promise<NoticePreferences> =>
    (await apiClient.put<NoticePreferences>("/notification-centre/preferences", { items })).data,

  getNoticeTypes: async (): Promise<NoticeType[]> => (await apiClient.get<NoticeType[]>("/admin/notice-types")).data,

  updateNoticeType: async (kind: string, request: UpdateNoticeTypeRequest): Promise<NoticeType> =>
    (await apiClient.put<NoticeType>(`/admin/notice-types/${encodeURIComponent(kind)}`, request)).data
};

/** "9+" past nine, so the badge never grows wider than the bell. */
export function badgeText(count: number): string {
  if (count <= 0) return "";
  return count > 9 ? "9+" : String(count);
}

/** The first action that opens a page (what clicking the notice itself does). */
export function primaryHref(notice: Notice): string | null {
  return notice.actions.find((a) => a.href !== null)?.href ?? null;
}
